/** Reminders, households, the store cache and the trigger feed.
 *
 * Four small screens rather than four files: each is a filter row over one
 * paginated table, and splitting them further would spread one idea across four
 * places.
 */

import { useState } from 'react'
import { adminApi, type ReminderState } from '../../lib/adminApi'
import {
  DataTable,
  Empty,
  ErrorNote,
  Filter,
  Loading,
  Pager,
  Pill,
  ago,
  dateTime,
  fmt,
  useAsync,
} from './parts'

const LIMIT = 25

// ------------------------------------------------------------- reminders
export function Reminders({ onOpenUser }: { onOpenUser: (id: string) => void }) {
  const [q, setQ] = useState('')
  const [state, setState] = useState<ReminderState>('all')
  const [offset, setOffset] = useState(0)
  const [busy, setBusy] = useState<string | null>(null)
  const [armed, setArmed] = useState<string | null>(null)

  const { data, error, loading, reload } = useAsync(
    () => adminApi.reminders({ q: q || undefined, state, limit: LIMIT, offset }),
    `reminders:${q}:${state}:${offset}`,
  )

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id)
    try {
      await fn()
      reload()
    } finally {
      setBusy(null)
      setArmed(null)
    }
  }

  return (
    <>
      <div className="staff-filters">
        <Filter label="Search">
          <input
            className="staff-input"
            value={q}
            placeholder="title, merchant or owner email"
            onChange={(e) => {
              setQ(e.target.value)
              setOffset(0)
            }}
          />
        </Filter>
        <Filter label="State">
          <select
            className="staff-input"
            value={state}
            onChange={(e) => {
              setState(e.target.value as ReminderState)
              setOffset(0)
            }}
          >
            <option value="all">all</option>
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="completed">completed</option>
            <option value="snoozed">snoozed</option>
            <option value="shared">shared with a household</option>
          </select>
        </Filter>
      </div>

      {error && <ErrorNote message={error} />}
      {loading && !data && <Loading what="reminders" />}
      {data && (
        <>
          <DataTable
            rows={data.items}
            rowId={(r) => r.id}
            empty="No reminders match those filters."
            columns={[
              { key: 't', head: 'Title', render: (r) => r.title },
              {
                key: 'm',
                head: 'Binds to',
                render: (r) => (
                  <div className="staff-who">
                    <strong>{r.merchant_label}</strong>
                    <span>
                      <code className="staff-code">{r.merchant_class}</code>
                    </span>
                  </div>
                ),
              },
              {
                key: 'o',
                head: 'Owner',
                render: (r) => (
                  <button className="staff-link" onClick={() => onOpenUser(r.owner_id)}>
                    {r.owner_email}
                  </button>
                ),
              },
              {
                key: 'h',
                head: 'Household',
                render: (r) => r.household_name ?? '—',
              },
              {
                key: 's',
                head: 'State',
                render: (r) =>
                  r.completed_at ? (
                    <Pill tone="green">done</Pill>
                  ) : r.active ? (
                    <Pill tone="teal">active</Pill>
                  ) : (
                    <Pill>paused</Pill>
                  ),
              },
              {
                key: 'n',
                head: 'Nudges',
                numeric: true,
                render: (r) => fmt(r.trigger_count),
              },
              { key: 'c', head: 'Created', render: (r) => ago(r.created_at) },
              {
                key: 'act',
                head: '',
                render: (r) => (
                  <div className="staff-rowacts">
                    {!r.completed_at && (
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={busy === r.id}
                        onClick={() =>
                          void act(r.id, () => adminApi.setReminderActive(r.id, !r.active))
                        }
                      >
                        {r.active ? 'Pause' : 'Resume'}
                      </button>
                    )}
                    {armed === r.id ? (
                      <button
                        className="btn btn--ghost btn--sm staff-danger"
                        disabled={busy === r.id}
                        onClick={() => void act(r.id, () => adminApi.deleteReminder(r.id))}
                      >
                        Confirm
                      </button>
                    ) : (
                      <button
                        className="btn btn--ghost btn--sm staff-danger"
                        onClick={() => setArmed(r.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
          />
          <Pager total={data.total} limit={data.limit} offset={data.offset} onOffset={setOffset} />
        </>
      )}
    </>
  )
}

// ------------------------------------------------------------ households
export function Households({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [q, setQ] = useState('')
  const [offset, setOffset] = useState(0)
  const [open, setOpen] = useState<string | null>(null)
  const [armed, setArmed] = useState<string | null>(null)

  const list = useAsync(
    () => adminApi.households({ q: q || undefined, limit: LIMIT, offset }),
    `households:${q}:${offset}`,
  )
  const detail = useAsync(
    () => (open ? adminApi.household(open) : Promise.resolve(null)),
    `household:${open ?? ''}`,
  )

  return (
    <div className={`staff-split${open ? ' is-open' : ''}`}>
      <div className="staff-split__main">
        <div className="staff-filters">
          <Filter label="Search">
            <input
              className="staff-input"
              value={q}
              placeholder="name or invite code"
              onChange={(e) => {
                setQ(e.target.value)
                setOffset(0)
              }}
            />
          </Filter>
        </div>

        {list.error && <ErrorNote message={list.error} />}
        {list.loading && !list.data && <Loading what="households" />}
        {list.data && (
          <>
            <DataTable
              rows={list.data.items}
              rowId={(r) => r.id}
              activeId={open}
              onRow={(r) => setOpen(r.id)}
              empty="No households yet."
              columns={[
                { key: 'n', head: 'Name', render: (r) => r.name },
                {
                  key: 'c',
                  head: 'Invite code',
                  render: (r) => <code className="staff-code">{r.invite_code}</code>,
                },
                { key: 'o', head: 'Owner', render: (r) => r.owner_email ?? '—' },
                { key: 'm', head: 'Members', numeric: true, render: (r) => fmt(r.member_count) },
                {
                  key: 'r',
                  head: 'Shared reminders',
                  numeric: true,
                  render: (r) => fmt(r.reminder_count),
                },
                { key: 'd', head: 'Created', render: (r) => ago(r.created_at) },
              ]}
            />
            <Pager
              total={list.data.total}
              limit={list.data.limit}
              offset={list.data.offset}
              onOffset={setOffset}
            />
          </>
        )}
      </div>

      {open && (
        <aside className="staff-panel glass" aria-label="Household detail">
          <header className="staff-panel__head">
            <div>
              <strong>{detail.data?.name ?? 'Household'}</strong>
              <span>{detail.data?.member_count ?? 0} members</span>
            </div>
            <button className="icon-btn" onClick={() => setOpen(null)} aria-label="Close panel">
              ✕
            </button>
          </header>

          {detail.error && <ErrorNote message={detail.error} />}
          {detail.data && (
            <>
              <DataTable
                rows={detail.data.members}
                rowId={(m) => m.user_id}
                empty="No members."
                columns={[
                  {
                    key: 'w',
                    head: 'Member',
                    render: (m) => (
                      <div className="staff-who">
                        <strong>{m.display_name}</strong>
                        <span>{m.email}</span>
                      </div>
                    ),
                  },
                  {
                    key: 'r',
                    head: 'Role',
                    render: (m) => <Pill tone={m.role === 'owner' ? 'teal' : 'slate'}>{m.role}</Pill>,
                  },
                  { key: 'j', head: 'Joined', render: (m) => ago(m.joined_at) },
                ]}
              />

              {isSuperAdmin && (
                <>
                  <h3 className="staff-panel__h staff-panel__h--danger">Delete household</h3>
                  <p className="hint">
                    Shared reminders cascade with the household — {fmt(detail.data.reminder_count)}{' '}
                    reminder(s) would go with it.
                  </p>
                  {armed === open ? (
                    <button
                      className="btn btn--ghost btn--sm staff-danger"
                      onClick={async () => {
                        await adminApi.deleteHousehold(open)
                        setArmed(null)
                        setOpen(null)
                        list.reload()
                      }}
                    >
                      Yes, delete “{detail.data.name}”
                    </button>
                  ) : (
                    <button
                      className="btn btn--ghost btn--sm staff-danger"
                      onClick={() => setArmed(open)}
                    >
                      Delete household
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </aside>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- stores
export function Stores({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [q, setQ] = useState('')
  const [merchantClass, setMerchantClass] = useState('')
  const [staleDays, setStaleDays] = useState('')
  const [offset, setOffset] = useState(0)
  const [purgeNote, setPurgeNote] = useState<string | null>(null)
  const [purgeError, setPurgeError] = useState<string | null>(null)
  const [armed, setArmed] = useState(false)

  const { data, error, loading, reload } = useAsync(
    () =>
      adminApi.stores({
        q: q || undefined,
        merchant_class: merchantClass || undefined,
        stale_days: staleDays ? Number(staleDays) : undefined,
        limit: LIMIT,
        offset,
      }),
    `stores:${q}:${merchantClass}:${staleDays}:${offset}`,
  )

  const canPurge = Boolean(merchantClass || staleDays)

  return (
    <>
      <div className="staff-filters">
        <Filter label="Search">
          <input
            className="staff-input"
            value={q}
            placeholder="name, brand or address"
            onChange={(e) => {
              setQ(e.target.value)
              setOffset(0)
            }}
          />
        </Filter>
        <Filter label="Merchant class">
          <input
            className="staff-input"
            value={merchantClass}
            placeholder="brand=Safeway"
            onChange={(e) => {
              setMerchantClass(e.target.value)
              setOffset(0)
            }}
          />
        </Filter>
        <Filter label="Older than (days)">
          <input
            className="staff-input staff-input--n"
            type="number"
            min={0}
            value={staleDays}
            onChange={(e) => {
              setStaleDays(e.target.value)
              setOffset(0)
            }}
          />
        </Filter>
      </div>

      {isSuperAdmin && (
        <div className="staff-purge glass">
          <div>
            <strong>Purge the cache</strong>
            <p className="hint">
              Drops the rows matching the filters above so the next position report re-fetches
              them. A purge with no filter is refused — it would cold-start every lookup, and a
              cold Overpass fetch is exactly what the cache exists to keep out of the request
              path.
            </p>
          </div>
          {armed ? (
            <div className="staff-actions">
              <button
                className="btn btn--ghost btn--sm staff-danger"
                onClick={async () => {
                  setPurgeError(null)
                  try {
                    const r = await adminApi.purgeStores({
                      merchant_class: merchantClass || undefined,
                      older_than_days: staleDays ? Number(staleDays) : undefined,
                    })
                    setPurgeNote(
                      `Purged ${r.stores_deleted} store(s) and ${r.queries_deleted} lookup record(s).`,
                    )
                    reload()
                  } catch (e) {
                    setPurgeError(e instanceof Error ? e.message : String(e))
                  } finally {
                    setArmed(false)
                  }
                }}
              >
                Confirm purge
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => setArmed(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="btn btn--ghost btn--sm staff-danger"
              disabled={!canPurge}
              title={canPurge ? undefined : 'Set a merchant class or an age first'}
              onClick={() => setArmed(true)}
            >
              Purge matching
            </button>
          )}
        </div>
      )}

      {purgeNote && (
        <p className="banner" role="status">
          {purgeNote}
        </p>
      )}
      {purgeError && <ErrorNote message={purgeError} />}
      {error && <ErrorNote message={error} />}
      {loading && !data && <Loading what="cached stores" />}
      {data && (
        <>
          <DataTable
            rows={data.items}
            rowId={(r) => r.id}
            empty="Nothing cached for those filters."
            columns={[
              { key: 'n', head: 'Name', render: (r) => r.name ?? '—' },
              { key: 'b', head: 'Brand', render: (r) => r.brand ?? '—' },
              {
                key: 'k',
                head: 'Tags',
                render: (r) =>
                  [r.shop && `shop=${r.shop}`, r.amenity && `amenity=${r.amenity}`,
                   r.leisure && `leisure=${r.leisure}`]
                    .filter(Boolean)
                    .map((t) => (
                      <code className="staff-code" key={String(t)}>
                        {t}
                      </code>
                    )),
              },
              { key: 'a', head: 'Address', render: (r) => r.address ?? '—' },
              { key: 'h', head: 'Hours', render: (r) => r.opening_hours ?? '—' },
              {
                key: 'l',
                head: 'Location',
                render: (r) => `${r.lat.toFixed(4)}, ${r.lon.toFixed(4)}`,
              },
              { key: 'f', head: 'Fetched', render: (r) => ago(r.fetched_at) },
            ]}
          />
          <Pager total={data.total} limit={data.limit} offset={data.offset} onOffset={setOffset} />
        </>
      )}
    </>
  )
}

// -------------------------------------------------------------- triggers
export function Triggers({ onOpenUser }: { onOpenUser: (id: string) => void }) {
  const [openOnly, setOpenOnly] = useState(false)
  const [offset, setOffset] = useState(0)

  const { data, error, loading } = useAsync(
    () => adminApi.triggers({ open_only: openOnly || undefined, limit: LIMIT, offset }),
    `triggers:${openOnly}:${offset}`,
  )

  return (
    <>
      <div className="staff-filters">
        <label className="staff-check">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => {
              setOpenOnly(e.target.checked)
              setOffset(0)
            }}
          />
          Only fences the user is still inside
        </label>
      </div>
      <p className="hint">
        A nudge fires at the reminder&rsquo;s radius and only re-arms once the user is past 1.2×
        it. A row with no clear time means they have not left yet — which is why the same nudge
        has not repeated.
      </p>

      {error && <ErrorNote message={error} />}
      {loading && !data && <Loading what="the trigger feed" />}
      {data && data.items.length === 0 && <Empty>Nothing has fired yet.</Empty>}
      {data && data.items.length > 0 && (
        <>
          <DataTable
            rows={data.items}
            rowId={(r) => r.id}
            empty="Nothing has fired yet."
            columns={[
              { key: 'f', head: 'Fired', render: (r) => dateTime(r.fired_at) },
              {
                key: 'u',
                head: 'User',
                render: (r) =>
                  r.user_email ? (
                    <button className="staff-link" onClick={() => onOpenUser(r.user_id)}>
                      {r.user_email}
                    </button>
                  ) : (
                    '—'
                  ),
              },
              { key: 'r', head: 'Reminder', render: (r) => r.reminder_title ?? '—' },
              { key: 's', head: 'Store', render: (r) => r.store_name ?? '—' },
              {
                key: 'c',
                head: 'Fence',
                render: (r) =>
                  r.cleared_at ? (
                    `cleared ${ago(r.cleared_at)}`
                  ) : (
                    <Pill tone="amber">still inside</Pill>
                  ),
              },
            ]}
          />
          <Pager total={data.total} limit={data.limit} offset={data.offset} onOffset={setOffset} />
        </>
      )}
    </>
  )
}
